import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { avatarUrl as av } from '../utils/imageUrl';
import LocationBanner from '../components/LocationBanner';
import { sendNewBookingAlert, sendBookingStatusAlert } from '../services/whatsapp.service';
import '../styles/BookingsPage.css';

const OCCASION = {
  birthday: { label: 'Ad günü',    icon: '🎂' },
  romantic:  { label: 'Romantik',  icon: '❤️' },
  business:  { label: 'İş görüşü', icon: '💼' },
  family:    { label: 'Ailə',      icon: '👨‍👩‍👧' },
};

function timeAgo(ts) {
  const m = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (m < 1)  return 'İndicə';
  if (m < 60) return `${m} dəq. əvvəl`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} saat əvvəl`;
  return `${Math.floor(h / 24)} gün əvvəl`;
}

function formatDate(d) {
  if (!d) return '';
  const [y, mo, day] = d.split('-');
  return `${day}.${mo}.${y}`;
}

export default function BookingsPage() {
  const { business }    = useAuth();
  const { locationId }  = useLocation();
  const [tab, setTab]   = useState('pending');
  const [bookings, setBookings]   = useState([]);
  const [allToday, setAllToday]   = useState(0);
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Pending count for badge (independent of tab) ─────────────────────────
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectQuick, setRejectQuick] = useState('');   // selected quick-pick
  const [rejectCustom, setRejectCustom] = useState(''); // free-text for 'Digər'
  const REJECT_REASONS = ['Masa dolu', 'Kapasite yoxdur', 'Həmin saatda bağlıyıq', 'Digər'];

  // ── Waitlist ──────────────────────────────────────────────────────────────
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [notifyingId, setNotifyingId] = useState(null);

  const loadWaitlist = async () => {
    const { data } = await supabase
      .from('waitlist')
      .select('*, users(full_name, avatar_url)')
      .eq('business_id', business.id)
      .in('status', ['waiting', 'notified'])
      .order('created_at', { ascending: true });
    setWaitlist(data || []);
    setWaitlistCount((data || []).filter(w => w.status === 'waiting').length);
  };

  const handleNotifyWaitlist = async (entry) => {
    setNotifyingId(entry.id);
    try {
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString();
      await supabase.from('waitlist').update({
        status: 'notified',
        notified_at: new Date().toISOString(),
        expires_at: expiresAt,
      }).eq('id', entry.id);

      const wlTitle = 'Masa boşaldı!';
      const wlBody  = `${business.name} restoranında masa sizin üçün ayrıldı. 15 dəqiqə ərzində təsdiq edin.`;
      await supabase.from('notifications').insert({
        user_id: entry.user_id,
        title: wlTitle,
        body:  wlBody,
        type: 'waitlist',
        data: { business_id: business.id, waitlist_id: entry.id },
        is_read: false,
      });
      await sendPushToUser(entry.user_id, wlTitle, wlBody, {
        type: 'waitlist',
        business_id: business.id,
      });

      showToast(`✅ ${entry.contact_name || 'Müştəri'}-ə bildiriş göndərildi`);
      loadWaitlist();
    } catch (e) {
      showToast('Xəta baş verdi', 'error');
    } finally {
      setNotifyingId(null);
    }
  };

  const handleRemoveWaitlist = async (id) => {
    await supabase.from('waitlist').update({ status: 'cancelled' }).eq('id', id);
    showToast('Siyahıdan silindi');
    loadWaitlist();
  };

  // Request browser notification permission once on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const playNotificationSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const gainNode = ctx.createGain();
      gainNode.connect(ctx.destination);

      const playTone = (freq, start, duration) => {
        const osc = ctx.createOscillator();
        osc.connect(gainNode);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
        gainNode.gain.setValueAtTime(0.4, ctx.currentTime + start);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + duration);
      };

      playTone(880, 0, 0.15);
      playTone(1100, 0.18, 0.15);
      playTone(1320, 0.36, 0.25);
    } catch (e) {}
  };

  const fireBrowserNotification = (title, body) => {
    playNotificationSound();
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/logo192.png' });
    }
  };

  const lastSeenIdRef = React.useRef(null);

  const checkNewBookings = React.useCallback(async () => {
    const { data } = await supabase
      .from('flash_bookings')
      .select('id, contact_name, party_size, guest_count, reservation_time')
      .eq('business_id', business.id)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(1);

    const latest = data?.[0];
    if (!latest) return;
    if (lastSeenIdRef.current === null) {
      lastSeenIdRef.current = latest.id;
      return;
    }
    if (latest.id !== lastSeenIdRef.current) {
      lastSeenIdRef.current = latest.id;
      const cname = latest.contact_name || 'Müştəri';
      const guests = latest.party_size || latest.guest_count || '';
      const ctime = latest.reservation_time?.slice(0, 5) || '';
      const msg = `${cname}${guests ? ' · ' + guests + ' nəfər' : ''}${ctime ? ' · ' + ctime : ''}`;
      showToast(`🔔 Yeni rezervasiya: ${msg}`, 'new');
      fireBrowserNotification('Yeni Rezervasiya!', msg);
      loadBookings();
      loadStats();
    }
  }, [business.id]);

  useEffect(() => {
    loadBookings();
    loadStats();
    loadWaitlist();

    // Realtime (filter olmadan — RLS tələb etmir, client-side filter)
    const sub = supabase
      .channel(`bp_bookings_${business.id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'flash_bookings',
      }, (payload) => {
        console.log('[RT] flash_bookings event:', payload.eventType, payload.new);
        const row = payload.new || {};
        console.log('[RT] id check:', row.business_id, '===', business.id, '->', String(row.business_id) === String(business.id));
        if (String(row.business_id) !== String(business.id)) return;

        if (payload.eventType === 'INSERT' && row.status === 'pending') {
          const cname = row.contact_name || 'Müştəri';
          const guests = row.party_size || row.guest_count || '';
          const ctime = row.reservation_time?.slice(0, 5) || '';
          const msg = `${cname}${guests ? ' · ' + guests + ' nəfər' : ''}${ctime ? ' · ' + ctime : ''}`;
          showToast(`🔔 Yeni rezervasiya: ${msg}`, 'new');
          fireBrowserNotification('Yeni Rezervasiya!', msg);
          lastSeenIdRef.current = row.id;
          // WhatsApp bildirişi → menecerin nömrəsinə
          if (business?.notification_whatsapp || business?.phone) {
            sendNewBookingAlert(business.notification_whatsapp || business.phone, row);
          }
        }
        if (row.status === 'cancelled' && payload.old?.status !== 'cancelled') {
          const cname = row.contact_name || 'Müştəri';
          const ctime = row.reservation_time?.slice(0, 5) || '';
          showToast(`❌ ${cname} rezervasiyanı ləğv etdi${ctime ? ' (' + ctime + ')' : ''}`, 'cancel');
        }
        loadBookings();
        loadStats();
      })
      .subscribe((status) => {
        console.log('[RT] subscription status:', status);
      });

    // Polling ehtiyatı — hər 15 saniyədə bir yoxla
    const poll = setInterval(checkNewBookings, 15000);

    return () => {
      sub.unsubscribe();
      clearInterval(poll);
    };
  }, [business, tab]);

  const loadStats = async () => {
    const today = new Date().toISOString().split('T')[0];

    const [pendRes, todayRes] = await Promise.all([
      supabase.from('flash_bookings').select('id', { count: 'exact', head: true })
        .eq('business_id', business.id).eq('status', 'pending'),
      supabase.from('flash_bookings').select('id', { count: 'exact', head: true })
        .eq('business_id', business.id).eq('reservation_date', today),
    ]);

    setPendingCount(pendRes.count || 0);
    setAllToday(todayRes.count || 0);
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      let q = supabase
        .from('flash_bookings')
        .select('*, users(id, full_name, avatar_url)')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });

      if (locationId) q = q.or(`location_id.eq.${locationId},location_id.is.null`);
      if (tab === 'pending') {
        q = q.eq('status', 'pending');
      } else {
        q = q.in('status', ['accepted', 'rejected', 'cancelled']);
      }

      const { data, error } = await q;
      if (error) throw error;
      setBookings(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const syncReservation = async (booking, status) => {
    if (!booking.user_id || !booking.business_id) return;
    try {
      let q = supabase.from('reservations').update({ status })
        .eq('user_id', booking.user_id).eq('business_id', booking.business_id);
      if (booking.reservation_date) q = q.eq('date', booking.reservation_date);
      await q;
    } catch {}
  };

  const sendPushToUser = async (userId, title, body, data = {}) => {
    const { data: tokenRow } = await supabase
      .from('push_tokens')
      .select('token')
      .eq('user_id', userId)
      .maybeSingle();

    if (!tokenRow?.token) return false;

    const { error } = await supabase.rpc('send_expo_push', {
      p_token: tokenRow.token,
      p_title: title,
      p_body: body,
      p_data: data,
    });

    if (error) console.error('[PUSH] rpc error:', error.message);
    return !error;
  };

  const handleRemindNoShow = async (booking) => {
    if (!booking.user_id) { showToast('İstifadəçi məlumatı yoxdur', 'error'); return; }
    const time = booking.reservation_time?.slice(0, 5) || '';
    const sent = await sendPushToUser(
      booking.user_id,
      '⏰ Rezervasiya xatırlatması',
      `${business.name} — bu gün saat ${time} rezervasiyanız var. Gəlirsinizmi?`,
      { type: 'no_show_reminder', booking_id: booking.id },
    );
    if (sent) showToast(`🔔 ${booking.contact_name || 'Müştəri'}-ə xatırlatma göndərildi`);
    else showToast('Push token tapılmadı', 'error');
  };

  const handleAccept = async (id) => {
    const b = bookings.find(x => x.id === id);
    setBookings(prev => prev.filter(x => x.id !== id));
    const { error } = await supabase.from('flash_bookings').update({ status: 'accepted' }).eq('id', id);
    if (error) { setBookings(prev => [b, ...prev]); showToast('Xəta baş verdi', 'error'); return; }
    if (b.table_id) {
      const today = new Date().toISOString().split('T')[0];
      if (b.reservation_date === today) {
        // Mark as reserved (not occupied — customer hasn't arrived yet)
        await supabase.from('tables').update({ status: 'reserved' }).eq('id', b.table_id);
      }
    }
    await syncReservation(b, 'confirmed');
    setPendingCount(c => Math.max(0, c - 1));
    showToast('Rezervasiya qəbul edildi');
    if (business?.notification_whatsapp || business?.phone) {
      sendBookingStatusAlert(business.notification_whatsapp || business.phone, b, 'accepted');
    }

    if (b.user_id) {
      const date = b.reservation_date || '';
      const time = b.reservation_time?.slice(0, 5) || '';
      const tableNum = b.table_number;
      const pushTitle = '✅ Rezervasiya Təsdiqləndi!';
      const pushBody  = `${business.name} — ${date} saat ${time}${tableNum ? `, Masa ${tableNum}` : ''}`;
      await sendPushToUser(
        b.user_id,
        pushTitle,
        pushBody,
        { type: 'reservation_confirmed', booking_id: b.id },
      );
      await supabase.from('notifications').insert({
        user_id: b.user_id,
        type:    'reservation_confirmed',
        title:   pushTitle,
        body:    pushBody,
        data:    { booking_id: b.id, business_id: business.id },
        is_read: false,
      });
    }
  };

  const handleReject = async (id, reason) => {
    const b = bookings.find(x => x.id === id);
    setBookings(prev => prev.filter(x => x.id !== id));
    const { error } = await supabase.from('flash_bookings')
      .update({ status: 'rejected', reject_reason: reason || null })
      .eq('id', id);
    if (error) { setBookings(prev => [b, ...prev]); showToast('Xəta baş verdi', 'error'); return; }
    if (b.table_id) {
      await supabase.from('tables').update({ status: 'available' }).eq('id', b.table_id);
    }
    await syncReservation(b, 'cancelled');
    setPendingCount(c => Math.max(0, c - 1));
    showToast('Rezervasiya rədd edildi');
    if (business?.notification_whatsapp || business?.phone) {
      sendBookingStatusAlert(business.notification_whatsapp || business.phone, b, 'rejected');
    }
    setRejectTarget(null); setRejectQuick(''); setRejectCustom('');

    if (b.user_id) {
      const reasonText = reason ? `: ${reason}` : '';
      const pushTitle = '❌ Rezervasiya Rədd Edildi';
      const pushBody  = `${business.name}${reasonText}`;
      await sendPushToUser(
        b.user_id,
        pushTitle,
        pushBody,
        { type: 'reservation_rejected', booking_id: b.id },
      );
      await supabase.from('notifications').insert({
        user_id: b.user_id,
        type:    'reservation_cancelled',
        title:   pushTitle,
        body:    pushBody,
        data:    { booking_id: b.id, business_id: business.id },
        is_read: false,
      });
    }
  };

  const acceptedToday = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return bookings.filter(b => b.status === 'accepted' && b.reservation_date === today).length;
  }, [bookings]);

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="bp-loading">
      <div className="bp-spinner" />
      <span>Yüklənir...</span>
    </div>
  );

  return (
    <div className="bookings-page">

      {/* Toast */}
      {toast && (
        <div className={`bookings-toast ${toast.type === 'error' ? 'toast-error' : toast.type === 'cancel' ? 'toast-cancel' : toast.type === 'new' ? 'toast-new' : 'toast-success'}`}>
          <span className="toast-icon">{toast.type === 'error' ? '✕' : '✓'}</span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bp-header">
        <div className="bp-header-left">
          <h1>Rezervasiya İdarəetməsi</h1>
          <p>Flash booking sorğularını real-time idarə edin</p>
        </div>
        <div className="bp-live">
          <span className="bp-live-dot" />
          Canlı izlənir
        </div>
      </div>

      <LocationBanner />

      {/* Stats */}
      <div className="bp-stats">
        <div className="bp-stat pending">
          <div className="bp-stat-icon">⏳</div>
          <div className="bp-stat-body">
            <div className="bp-stat-val">{pendingCount}</div>
            <div className="bp-stat-lbl">Gözləyən</div>
          </div>
        </div>
        <div className="bp-stat today">
          <div className="bp-stat-icon">📅</div>
          <div className="bp-stat-body">
            <div className="bp-stat-val">{allToday}</div>
            <div className="bp-stat-lbl">Bu gün</div>
          </div>
        </div>
        <div className="bp-stat done">
          <div className="bp-stat-icon">✅</div>
          <div className="bp-stat-body">
            <div className="bp-stat-val">{acceptedToday}</div>
            <div className="bp-stat-lbl">Qəbul edildi</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bp-tabs">
        <button className={`bp-tab${tab === 'pending' ? ' active' : ''}`} onClick={() => setTab('pending')}>
          Gələnlər
          {pendingCount > 0 && <span className="bp-tab-badge">{pendingCount}</span>}
        </button>
        <button className={`bp-tab${tab === 'waitlist' ? ' active' : ''}`} onClick={() => { setTab('waitlist'); loadWaitlist(); }}>
          Gözləmə
          {waitlistCount > 0 && <span className="bp-tab-badge">{waitlistCount}</span>}
        </button>
        <button className={`bp-tab${tab === 'history' ? ' active' : ''}`} onClick={() => setTab('history')}>
          Tarixçə
        </button>
      </div>

      {/* Waitlist tab */}
      {tab === 'waitlist' && (
        <div className="bp-list">
          {waitlist.length === 0 ? (
            <div className="bp-empty">
              <div className="bp-empty-emoji">⏳</div>
              <h3>Gözləmə siyahısı boşdur</h3>
              <p>Müştərilər masa dolu olduqda bura yazılacaq</p>
            </div>
          ) : (
            waitlist.map(entry => (
              <div key={entry.id} className={`bp-card status-${entry.status === 'notified' ? 'accepted' : 'pending'}`}>
                <div className="bp-card-top">
                  <div className="bp-avatar-fallback">
                    {(entry.contact_name || entry.users?.full_name || '?')[0].toUpperCase()}
                  </div>
                  <div className="bp-user-info">
                    <p className="bp-user-name">{entry.contact_name || entry.users?.full_name || 'Naməlum'}</p>
                    <p className="bp-user-phone">📞 {entry.contact_phone || '—'}</p>
                  </div>
                  <span className={`bp-status-pill ${entry.status === 'notified' ? 'accepted' : 'pending'}`}>
                    {entry.status === 'notified' ? '🔔 Bildirildi' : '⏳ Gözləyir'}
                  </span>
                </div>
                <div className="bp-card-body">
                  <div className="bp-chips">
                    <span className="bp-chip people">👥 <strong>{entry.party_size}</strong> nəfər</span>
                    <span className="bp-chip">🕐 {new Date(entry.created_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}</span>
                    {entry.note && <span className="bp-chip">💬 {entry.note}</span>}
                    {entry.status === 'notified' && entry.expires_at && (
                      <span className="bp-chip" style={{ color: '#F59E0B' }}>
                        ⏱ {new Date(entry.expires_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}-dək
                      </span>
                    )}
                  </div>
                </div>
                <div className="bp-actions">
                  {entry.status === 'waiting' && (
                    <button
                      className="bp-btn-accept"
                      onClick={() => handleNotifyWaitlist(entry)}
                      disabled={notifyingId === entry.id}
                    >
                      {notifyingId === entry.id ? 'Göndərilir...' : '🔔 Masa boşaldı — Xəbər ver'}
                    </button>
                  )}
                  <button
                    className="bp-btn-reject"
                    onClick={() => handleRemoveWaitlist(entry.id)}
                  >
                    Siyahıdan sil
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* List */}
      {tab !== 'waitlist' && <div className="bp-list">
        {bookings.length === 0 ? (
          <div className="bp-empty">
            <div className="bp-empty-emoji">{tab === 'pending' ? '📭' : '🗂️'}</div>
            <h3>{tab === 'pending' ? 'Gözləyən sorğu yoxdur' : 'Tarixçə boşdur'}</h3>
            <p>
              {tab === 'pending'
                ? 'Müştəri rezervasiya etdikdə burada görünəcək'
                : 'Qəbul və ya rədd edilmiş sorğular burada göstəriləcək'}
            </p>
          </div>
        ) : (
          bookings.map((b) => {
            const name    = b.contact_name || b.users?.full_name || 'Naməlum';
            const phone   = b.contact_phone || '—';
            const guests  = b.party_size ?? b.guest_count ?? '—';
            const initial = name[0]?.toUpperCase() || '?';
            const occ     = b.occasion && b.occasion !== 'regular' ? OCCASION[b.occasion] : null;
            const statusClass = b.status === 'accepted' ? 'accepted' : b.status === 'rejected' ? 'rejected' : b.status === 'cancelled' ? 'cancelled' : 'pending';
            const pillLabel = b.status === 'cancelled'
              ? (b.reject_reason ? 'Kafe ləğv etdi' : 'Müştəri ləğv etdi')
              : { pending: 'Gözləyir', accepted: 'Qəbul edilib', rejected: 'Kafe rədd etdi' }[b.status] || b.status;

            return (
              <div key={b.id} className={`bp-card status-${statusClass}`}>

                {/* Top */}
                <div className="bp-card-top">
                  {b.users?.avatar_url
                    ? <img src={av(b.users.avatar_url)} alt={name} className="bp-avatar" />
                    : <div className="bp-avatar-fallback">{initial}</div>
                  }
                  <div className="bp-user-info">
                    <p className="bp-user-name">{name}</p>
                    <p className="bp-user-phone">📞 {phone}</p>
                  </div>
                  {tab === 'history' && (
                    <span className={`bp-status-pill ${statusClass}`}>
                      {pillLabel}
                    </span>
                  )}
                </div>

                {/* Body */}
                <div className="bp-card-body">
                  <div className="bp-chips">
                    <span className="bp-chip people">
                      👥 <strong>{guests}</strong> nəfər
                    </span>

                    {b.reservation_date && (
                      <span className="bp-chip">
                        📅 {formatDate(b.reservation_date)}
                        {b.reservation_time ? ` · ${b.reservation_time.slice(0,5)}${b.end_time ? ' – ' + b.end_time.slice(0,5) : ''}` : ''}
                      </span>
                    )}

                    {b.table_number && (
                      <span className="bp-chip table">
                        🪑 Masa {b.table_number}
                      </span>
                    )}

                    {occ && (
                      <span className="bp-chip occasion">
                        {occ.icon} {occ.label}
                      </span>
                    )}

                    <span className="bp-chip time">
                      🕐 {timeAgo(b.created_at)}
                    </span>

                    {b.booking_code && (
                      <span className="bp-chip code">
                        #{b.booking_code}
                      </span>
                    )}

                    {b.special_requests && (
                      <div className="bp-chip-note">
                        💬 <span>{b.special_requests}</span>
                      </div>
                    )}
                    {b.cancel_reason && (
                      <div className="bp-chip-note" style={{borderLeftColor:'#7c3aed'}}>
                        ❌ Ləğv səbəbi: <span>{b.cancel_reason}</span>
                      </div>
                    )}
                    {b.reject_reason && (
                      <div className="bp-chip-note" style={{borderLeftColor:'#ef4444'}}>
                        🚫 Rədd səbəbi: <span>{b.reject_reason}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {tab === 'pending' && (
                  <div className="bp-card-actions">
                    <button className="bp-btn accept" onClick={() => handleAccept(b.id)}>
                      ✓ Qəbul Et
                    </button>
                    <button className="bp-btn reject" onClick={() => { setRejectTarget(b.id); setRejectQuick(''); setRejectCustom(''); }}>
                      ✕ Rədd Et
                    </button>
                  </div>
                )}
                {tab === 'history' && b.status === 'accepted' && b.user_id && (
                  <div className="bp-card-actions">
                    <button className="bp-btn-remind" onClick={() => handleRemindNoShow(b)}>
                      🔔 Xatırlat
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>}

      {/* Reject Reason Modal */}
      {rejectTarget && (
        <div className="bp-modal-overlay" onClick={() => setRejectTarget(null)}>
          <div className="bp-modal-card" onClick={e => e.stopPropagation()}>
            <h3 className="bp-modal-title">Rədd etmə səbəbi</h3>
            <p className="bp-modal-sub">Müştəriyə göndəriləcək</p>
            <div className="bp-modal-reasons">
              {REJECT_REASONS.map(r => (
                <button
                  key={r}
                  className={`bp-reason-btn${rejectQuick === r ? ' selected' : ''}`}
                  onClick={() => { setRejectQuick(r); if (r !== 'Digər') setRejectCustom(''); }}
                >
                  {rejectQuick === r ? '● ' : '○ '}{r}
                </button>
              ))}
            </div>
            {rejectQuick === 'Digər' && (
              <textarea
                className="bp-reason-input"
                placeholder="Səbəbi yazın..."
                rows={3}
                value={rejectCustom}
                onChange={e => setRejectCustom(e.target.value)}
                autoFocus
              />
            )}
            <div className="bp-modal-actions">
              <button className="bp-modal-btn cancel" onClick={() => setRejectTarget(null)}>Ləğv et</button>
              <button
                className="bp-modal-btn confirm"
                disabled={!rejectQuick || (rejectQuick === 'Digər' && !rejectCustom.trim())}
                onClick={() => handleReject(rejectTarget, rejectQuick === 'Digər' ? rejectCustom.trim() : rejectQuick)}
              >
                Rədd Et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
